import { GeoveloInlineIcon } from '@geovelo-frontends/commons';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Link } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import React, { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import {
  ForgotPasswordDataFragment,
  SignInDataFragment,
  SignUpDataFragment,
} from '../../graphql-types';
import Description from '../components/slices/description';
import Title from '../components/slices/title';
import SSO from '../components/sso';

function SignInLayout({
  disableSSO,
  signUp,
  titleKey,
  descriptionKey,
  data,
  children,
}: {
  data?: Partial<SignInDataFragment | SignUpDataFragment | ForgotPasswordDataFragment>;
  descriptionKey?: string;
  disableSSO?: boolean;
  children: ReactNode;
  signUp?: boolean;
  titleKey: string;
}): React.JSX.Element {
  const [loading, setLoading] = useState(false);
  const {
    t,
    i18n: { language: currentLanguage },
  } = useTranslation();

  if (!data) return <></>;

  const { form_title, image: _image } = data;
  const image = _image && getImage(_image);

  return (
    <Box
      display="flex"
      flexDirection="row"
      sx={{
        height: '100vh',
        minHeight: '100vh',
        '@supports (-webkit-touch-callout: none)': {
          height: '-webkit-fill-available',
          minHeight: '-webkit-fill-available',
        },
      }}
    >
      <Box flexGrow={1} sx={{ overflowY: 'auto' }}>
        <Box
          alignItems="center"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          minHeight="100%"
          paddingY={5}
        >
          {loading ? (
            <Box alignItems="center" display="flex" flexDirection="column" gap={2}>
              <CircularProgress />
              <Typography align="center">
                {t(signUp ? 'geovelo.sign_up.loading' : 'geovelo.sign_in.loading')}
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                display="flex"
                flexDirection="column"
                gap={3}
                maxWidth="calc(100% - 32px)"
                width={378}
              >
                <Box flexShrink={0} height={32}>
                  <StyledLink
                    style={{ display: 'inline-block', height: 32 }}
                    title="Geovelo"
                    to={`/${currentLanguage}`}
                  >
                    <GeoveloInlineIcon sx={{ height: 32, width: 'auto' }} />
                  </StyledLink>
                </Box>
                <Title
                  component="h1"
                  data={{ text: form_title?.text || t(titleKey) }}
                  fontSize="2em"
                />
                {'form_description' in data && (
                  <Description
                    data={{
                      text: data.form_description?.text || (descriptionKey && t(descriptionKey)),
                    }}
                    fontSize="1em"
                  />
                )}
                <Box display="flex" flexDirection="column" gap={2}>
                  {!disableSSO && (
                    <>
                      <SSO setLoading={setLoading} signUp={signUp} />
                      <Typography align="center" fontWeight={600}>
                        {t('commons.sign_in_dialog.sign_in_with.other')}
                      </Typography>
                    </>
                  )}
                  {children}
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>
      <Box display={{ xs: 'none', md: 'block' }} flexShrink={0} width="40%">
        {image && (
          <GatsbyImage
            alt=""
            image={image}
            objectFit="cover"
            objectPosition="center center"
            style={{ height: '100%', width: '100%' }}
          />
        )}
      </Box>
    </Box>
  );
}

const StyledLink = styled(Link)`
  color: inherit;
  text-decoration: none;
`;

export default SignInLayout;
